import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import SortableTable from '../../../shared/components/SortableTable';
import ViewToolbar from '../../../shared/components/ViewToolbar';
import TimeRangeSelect from '../../../shared/components/TimeRangeSelect';
import { ACTION_LOG_COLUMNS } from './opsitemColumns';
import ActionLogDetailPanel from './ActionLogDetailPanel';
import FilterRailSlot from './FilterRailSlot';
import MasterDetailLayout from '../../../shared/components/MasterDetailLayout';
import { useViewport } from '../../../shared/hooks/useViewport';
import FacetedFilterRail, { applyFacetFilters, emptyFacetState, type FacetFilterState } from '../../../shared/components/FacetedFilterRail';
import { OPSITEM_FACETS, matchOpsItemSearch } from '../utils/opsitemFacets';
import { ACTION_LOG_PRESETS, withinTimeRange } from '../utils/actionLogTimeRange';
import { useActionLog } from '../hooks/useActionLog';
import { useActionLogDeepLink } from '../hooks/useActionLogDeepLink';
import { useAuth } from '../../auth/context/AuthContext';
import type { ActionLogEntry } from '../../../types/backend';

interface ActionLogTabContentProps {
  customerId: string;
  onCountChange: (count: number) => void;
  onStartInvestigation: (entry: ActionLogEntry) => void;
  initialOpsItemId?: string | null;
}

const ActionLogTabContent: React.FC<ActionLogTabContentProps> = ({
  customerId,
  onCountChange,
  onStartInvestigation,
  initialOpsItemId,
}) => {
  const { entries, loading, loadingMore, error, hasMore, setError, reload, loadMore, loadEntry, closeItem, deleteItem } = useActionLog(customerId);
  const { isAdmin } = useAuth();
  // The open item's detail shares the row with the table, so below `lg` the rail gives up its width.
  const railInDrawer = !useViewport().isDesktop;

  const [filters, setFilters] = useState<FacetFilterState>(emptyFacetState);
  const [timeRange, setTimeRange] = useState<string | undefined>('all');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  const timeFilteredEntries = useMemo(
    () => entries.filter((e) => withinTimeRange(e.created_at, timeRange, startTime, endTime)),
    [entries, timeRange, startTime, endTime],
  );

  const filteredEntries = useMemo(
    () => applyFacetFilters(timeFilteredEntries, OPSITEM_FACETS, filters, matchOpsItemSearch),
    [timeFilteredEntries, filters],
  );

  useEffect(() => {
    onCountChange(entries.length);
  }, [entries.length, onCountChange]);

  const { selectedEntry, select: setSelectedEntry } = useActionLogDeepLink(customerId, initialOpsItemId, entries, loadEntry);

  const closeEntry = () => setSelectedEntry(null);
  const list = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <SortableTable
        columns={ACTION_LOG_COLUMNS}
        data={filteredEntries}
        rowKey={(e) => e.opsitem_id}
        selectedKey={selectedEntry?.opsitem_id}
        onRowClick={setSelectedEntry}
        loading={loading}
        emptyMessage="No action log items match your filters"
        defaultSortKey="created_at"
      />

      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={loadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={16} /> : undefined}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
    </Box>
  );
  const detail = selectedEntry && (
    <ActionLogDetailPanel
      entry={selectedEntry}
      customerId={customerId}
      onClose={closeEntry}
      isAdmin={isAdmin}
      onCloseItem={closeItem}
      onDeleteItem={deleteItem}
      onStartInvestigation={onStartInvestigation}
    />
  );

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <ViewToolbar
        count={filteredEntries.length === entries.length
          ? `${entries.length} action log items`
          : `${filteredEntries.length} of ${entries.length} action log items`}
        onRefresh={reload}
        loading={loading}
      >
        <TimeRangeSelect
          presets={ACTION_LOG_PRESETS}
          timeRange={timeRange}
          startTime={startTime}
          endTime={endTime}
          onSelectPreset={(value) => {
            if (value === 'custom') {
              setTimeRange(undefined);
              setStartTime('');
              setEndTime('');
            } else {
              setTimeRange(value);
            }
          }}
          onStartChange={setStartTime}
          onEndChange={setEndTime}
        />
      </ViewToolbar>

      {error && (
        <InlineAlert severity="error" onClose={() => setError(null)} sx={{ mx: 2, mt: 1, flexShrink: 0 }}>
          {error}
        </InlineAlert>
      )}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: railInDrawer ? 'column' : 'row', overflow: 'hidden', minHeight: 0 }}>
        <FilterRailSlot state={filters}>
          <FacetedFilterRail
            entities={timeFilteredEntries}
            facets={OPSITEM_FACETS}
            state={filters}
            setState={setFilters}
            matchSearch={matchOpsItemSearch}
            searchPlaceholder="Search action log items…"
          />
        </FilterRailSlot>

        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
          {/* Below `md` an opened item takes the view; from `md` up it opens beside the table. */}
          <MasterDetailLayout
            resizable
            list={list}
            detail={detail}
            hasSelection={selectedEntry !== null}
            onBack={closeEntry}
            backLabel="Back to the action log"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ActionLogTabContent;
